import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Link,
	useLocation,
	useParams
} from 'react-router-dom';
import {
	Header,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../services/utils';
import {
	validateEmail,
	validatePhone,
	validateZip
} from '../../../../../services/validation';

import useForm from '../../../../../hooks/useForm';

import {
	createAgency,
	getAgency,
	updateAgency
} from '../../../../../api/nora/agencies';

import {
	useAgencyState,
	useAgencyAction
} from '../../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Alert from '../../../../Alert';
import CoordinationTab from './CoordinationTab';
import EmailValidationInfoPopup from '../../../../Form/EmailValidationInfoPopup';
import PhoneValidationInfoPopup from '../../../../Form/PhoneValidationInfoPopup';
import ProfileTab from './ProfileTab';
import ProgramsTab from './ProgramsTab';
import StaffTab from './StaffTab';
import LocationTab from './LocationsTab';

const AgencyDetails = () => {
	const { id } = useParams();
	const { state: navigationState } = useLocation();

	const {
		agency,
		initialized
	} = useAgencyState();
	const {
		setAgency,
		clearAgency
	} = useAgencyAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setNavLock
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( navigationState
		? navigationState.activeIndex
		: 0 );
	const [ unsavedProgressAlert, setUnsavedProgressAlert ] = useState( false );
	const [ agencySavedAlert, setAgencySavedAlert ] = useState( false );
	// used to store the tab index when showing the unsavedProgressAlert
	const [ pendingIndex, setPendingIndex ] = useState();
	const [ hideMenu, setHideMenu ] = useState( false );

	const {
		formData,
		hasChanged: formDataHasChanged,
		resetFormData,
		setFormData,
		updateField
	} = useForm();

	useEffect( () => {
		navigationState?.to
			? setInfobar( <span><Link to={ navigationState.to }>{ navigationState.text || '\u2190\u00A0\u00A0Back' }</Link></span> )
			: setInfobar( <span>Agencies &nbsp;&#9656;&nbsp; <Link to="/agencies/directory">Directory</Link> &nbsp;&#9656;&nbsp; { agency?.name ? agency.name : 'New Agency' }</span> );
	}, [
		agency,
		navigationState,
		setInfobar
	] );

	useEffect( () => {
		if ( !initialized ) {
			setInfobarLoader( 'Please wait...' );
		} else if ( id > 0 ) { // not a new agency
			( async () => {
				setInfobarLoader( 'Retrieving Agency Info...' );

				try {
					const response = await getAgency( id );

					await setAgency( response );
				} catch ( error ) {
					setApiErrorAlert( error );
				}
			} )();
		}

		return () => {
			clearAgency();
		};
	}, [
		clearAgency,
		id,
		initialized,
		setAgency,
		setApiErrorAlert,
		setInfobarLoader
	] );

	useEffect( () => {
		if ( formDataHasChanged ) {
			setNavLock( true );
		} else {
			setNavLock( false );
		}
	}, [
		formDataHasChanged,
		setNavLock
	] );

	useEffect( () => {
		if ( agency ) { // if it is not a new agency
			setFormData( {
				...agency,
				type: agency.type.id,
				phone: agency.phone.number,
				ext: agency.phone.extension,
				SMSEnabled: agency.phone.isSMSEnabled,
				fax: agency.fax.number,
				...navigationState && navigationState.initialData
			} );
		} else if ( id === '0' ) {
			setFormData( {
				id: 0,
				...navigationState && navigationState.initialData
			} );
		}
	}, [
		agency,
		id,
		navigationState,
		setFormData
	] );

	useEffect( () => {
		if ( initialized && Object.keys( formData ).length ) {
			setIsLoading( false );
			setInfobarLoader( '' );
		}
	}, [
		formData,
		initialized,
		setInfobarLoader
	] );

	const errors = useMemo( () => ( {
		name: [
			{
				error: !formData.name,
				message: 'Agency Name is a required field.',
				fieldLabel: 'Agency Name'
			}
		],
		type: [
			{
				error: !formData.type,
				message: 'Agency Type is a required field.',
				fieldLabel: 'Agency Type'
			}
		],
		zip: [
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		],
		fax: [
			{
				error: Boolean( formData.fax ) && !validatePhone( formData.fax ),
				message: <>Please enter a valid U.S. Phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Fax'
			}
		]
	} ), [
		formData.email,
		formData.fax,
		formData.name,
		formData.phone,
		formData.type,
		formData.zip
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const handleSave = useCallback( async cb => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			let {
				type,
				phone,
				ext,
				SMSEnabled,
				fax,
				...rest
			} = formData;
			const data = {
				...rest,
				type: { id: type },
				phone: {
					number: phone,
					extension: ext,
					isSMSEnabled: SMSEnabled
				},
				fax: { number: fax }
			};

			try {
				setNavLock( false );

				if ( formData.id === 0 ) {
					const response = await createAgency( data );

					setAgency( response );
				} else {
					const response = await updateAgency( data );

					setAgency( response );
				}

				// reinitializeFormData();
				setAgencySavedAlert( true );

				cb?.();
			} catch ( error ) {
				setNavLock( true );
				setApiErrorAlert( error );
			}
		}
	}, [
		forceValidation,
		formData,
		setAgency,
		setApiErrorAlert,
		setNavLock,
		validate
	] );

	const menu = useMemo( () => ( {
		className: 'no-print',
		pointing: true,
		secondary: true,
		floated: true,
		style: {
			width: '100%',
			paddingTop: '0.5rem',
			marginBottom: '3rem'
		}
	} ), [] );

	const renderProfileTab = useCallback( () => (
		<ProfileTab
			errors={ errors }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged={ formDataHasChanged }
			handleSave={ handleSave }
			resetFormData={ resetFormData }
			updateField={ updateField }
			validate={ validate }
		/>
	), [
		errors,
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		resetFormData,
		updateField,
		validate
	] );

	const renderCoordinationTab = useCallback( ( { activeIndex } ) => <CoordinationTab activeIndex={ activeIndex } />, [] );

	const renderLocationsTab = useCallback( ( { activeIndex } ) => <LocationTab activeIndex={ activeIndex } />, [] );

	const renderProgramsTab = useCallback(
		( { activeIndex } ) => (
			<ProgramsTab
				activeIndex={ activeIndex }
				hideMenu={ setHideMenu }
			/>
		),
		[]
	);

	const renderStaffTab = useCallback( () => <StaffTab />, [] );

	const panes = useMemo( () => [
		{
			menuItem: {
				key: 'profile',
				content: 'Profile',
				disabled: hideMenu,
				style: {
					borderColor: hideMenu
						? 'transparent'
						: null,
					fontWeight: hideMenu
						? 'normal'
						: null
				}
			},
			render: renderProfileTab
		},
		...agency?.programs?.length
			? [
				{
					menuItem: {
						key: 'programs',
						content: 'Programs',
						disabled: hideMenu,
						style: {
							borderColor: hideMenu
								? 'transparent'
								: null,
							fontWeight: hideMenu
								? 'normal'
								: null
						}
					},
					render: renderProgramsTab
				}
			]
			: [],
		{
			menuItem: {
				key: 'coordination',
				content: 'Coordination',
				disabled: hideMenu,
				style: {
					borderColor: hideMenu
						? 'transparent'
						: null,
					fontWeight: hideMenu
						? 'normal'
						: null
				}
			},
			render: renderCoordinationTab
		},
		{
			menuItem: {
				key: 'locations',
				content: 'Locations',
				disabled: hideMenu,
				style: {
					borderColor: hideMenu
						? 'transparent'
						: null,
					fontWeight: hideMenu
						? 'normal'
						: null
				}
			},
			render: renderLocationsTab
		},
		{
			menuItem: {
				key: 'staff',
				content: 'Staff',
				disabled: hideMenu,
				style: {
					borderColor: hideMenu
						? 'transparent'
						: null,
					fontWeight: hideMenu
						? 'normal'
						: null
				}
			},
			render: renderStaffTab
		}
	], [
		agency,
		hideMenu,
		renderCoordinationTab,
		renderLocationsTab,
		renderProfileTab,
		renderProgramsTab,
		renderStaffTab
	] );

	const handleUnsavedProgress = useCallback( () => {
		handleSave();
		setUnsavedProgressAlert( false );
		setActiveIndex( pendingIndex );
	}, [
		handleSave,
		pendingIndex
	] );

	const closeUnsavedProgressAlert = useCallback( () => {
		setUnsavedProgressAlert( false );
	}, [] );

	const closeAgencySavedAlert = useCallback( () => {
		setAgencySavedAlert( false );
	}, [] );

	const handleTabChange = useCallback( ( e, { activeIndex } ) => {
		if ( hideMenu ) {
			return;
		}

		if ( !validate() ) {
			if ( forceValidation ) {
				setForceValidation( false );
			}

			if ( formDataHasChanged ) {
				setPendingIndex( activeIndex );
				setUnsavedProgressAlert( true );
			} else {
				setActiveIndex( activeIndex );
			}
		} else if ( !forceValidation ) {
			setForceValidation( true );
		}
	}, [
		forceValidation,
		formDataHasChanged,
		hideMenu,
		validate
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="detail-page">
			{ log( 'agencyDetails' ) }
			<Tab
				activeIndex={ activeIndex }
				menu={ menu }
				onTabChange={ handleTabChange }
				panes={ panes }
			/>
			<Alert
				action={ handleUnsavedProgress }
				actionText="Save and Continue"
				name="agency-unsaved-progress-confirm-alert"
				message="Please save your progress before continuing."
				open={ unsavedProgressAlert }
				closeModal={ closeUnsavedProgressAlert }
				type="confirm"
			/>
			<Alert
				closeModal={ closeAgencySavedAlert }
				message={
					<Header
						as="h1"
						color="pink"
					>
					Saved!
					</Header>
				}
				name="agency-saved-notification"
				open={ agencySavedAlert }
				type="notification"
			/>
		</div>
	);
};

export default memo( AgencyDetails );
